/**
 * Submit router: pre-tournament direct Firestore write vs post-kickoff submitRoster callable.
 * Shows reset confirmation when replacing an existing list mid-tournament.
 */
import { QuestionIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Show,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  countryCategory,
  countryListValid,
  tournamentCountriesInfo,
  type Country,
} from '@/models/country';
import { updateTeamSelectionData, updateUser } from '@/models/user';
import { EDIT_LIST_TAB_RANK, EDIT_LIST_TAB_SELECT } from '@/constants/editListMobile';
import { MODAL_FOOTER_PB, MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { type Dict } from '../../helpers';
import { EditCountryListView } from '..';
import { FirebaseContext } from '@/session/FirebaseContext';
import { RulesModal } from './RulesModal';
import { ConfirmModal } from './ConfirmModal';
import { type gameUpdate, sentinelType } from '@/models/gameUpdate';
import { logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';

function selectionProgressSummary(countryList: Country[]): string {
  const teamCount = countryList.length;
  const seedCount = countryList.filter(
    (c) => tournamentCountriesInfo[c].category === countryCategory.Favourite,
  ).length;
  return `${teamCount}/10 teams · ${seedCount}/4 seeds`;
}

export function EditCountryListModal(props: {
  isOpen: boolean;
  onClose: () => void;
  getStartingList: () => Country[];
  prompt?: boolean;
  gameUpdates: Dict<gameUpdate>;
}) {
  const [tabIndex, setTabIndex] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [countryList, setCountryList] = useState<Country[]>(props.getStartingList());
  const rulesDisclosure = useDisclosure();
  const confirmEditDisclosure = useDisclosure();
  const ctx = useContext(FirebaseContext);

  const tournamentStarted = Object.keys(props.gameUpdates ?? {}).includes(sentinelType.tournamentStarted);
  const latecomersAllowed = Object.keys(props.gameUpdates ?? {}).includes(sentinelType.latecomersAllowed);

  const isLg = useBreakpointValue({ base: false, lg: true });
  const modalCentered = useBreakpointValue({ base: undefined, md: true });
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    setCountryList(props.getStartingList());
    setTabIndex(0);
  }, [props.isOpen]);

  const listValid = countryListValid(countryList);
  const inputValid = listValid;
  const progressSummary = useMemo(() => selectionProgressSummary(countryList), [countryList]);

  function isFinalTab() {
    return tabIndex === 1;
  }

  // --- Submit routing: updateTeamSelectionData vs submitRoster ---

  async function submitTeam(): Promise<void> {
    setIsBusy(true);
    logEvent(ctx.analytics, 'submit_team', { late_submission: latecomersAllowed });
    if (tournamentStarted) {
      // Once the tournament has started, all list changes (late first submission or reset)
      // must go through the server so the MatchDay baseline (mdReset) is set correctly.
      await httpsCallable(ctx.funcs, 'submitRoster')({ countryList });
    } else {
      await updateTeamSelectionData(ctx.db, ctx.user!.uid, {
        countryList: countryList,
        spectator: false,
      });
    }
    await updateUser(ctx.db, ctx.user!.uid, {
      requiresShareUpdate: true,
    });
    props.onClose();
    setIsBusy(false);
  }

  async function handleSubmit(): Promise<void> {
    if (!tournamentStarted) {
      await submitTeam();
    } else if (props.getStartingList().length === 10) {
      // Replacing an existing list mid-tournament is a RESET: it forfeits all points so far.
      confirmEditDisclosure.onOpen();
    } else {
      // Late first submission (no existing list).
      await submitTeam();
    }
  }

  // --- Modal layout: desktop single view vs mobile selection/ranking tabs ---

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        scrollBehavior='inside'
        isCentered={modalCentered}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          maxW={{ base: '100%', lg: 'min(96vw, 1400px)' }}
          w='full'
          mx='auto'
          maxH={{ base: '100%', lg: '92vh' }}
          my={{ base: 0, lg: 'auto' }}
          display='flex'
          flexDirection='column'
          borderRadius={{ base: 0, lg: 'md' }}
        >
          <ModalCloseButton zIndex={2} mt={2} mr={2} />
          <ModalBody
            p={{ base: 3, lg: 3 }}
            pt={{ base: MODAL_HEADER_PT.base, lg: 3 }}
            flex='1'
            minH={0}
            display='flex'
            flexDirection='column'
            overflow='hidden'
          >
            {props.prompt ? (
              <Show below='lg'>
                <Text mb={2} fontSize='sm' color={mutedColor}>
                  You can do this later if you&apos;d prefer to.
                </Text>
              </Show>
            ) : null}
            {isLg ? (
              <Box flex={1} minH={0} display='flex' flexDirection='column' overflow='hidden'>
                <EditCountryListView countryList={countryList} setCountryList={setCountryList} />
              </Box>
            ) : (
              <Tabs
                index={tabIndex}
                onChange={setTabIndex}
                display='flex'
                flexDirection='column'
                flex='1'
                minH={0}
                h='full'
                variant='enclosed'
                colorScheme='green'
                size='sm'
              >
                <TabList flexShrink={0}>
                  <Tab fontSize='sm'>{EDIT_LIST_TAB_SELECT}</Tab>
                  <Tab fontSize='sm' isDisabled={!listValid} title={!listValid ? 'Select 10 teams first' : undefined}>
                    {EDIT_LIST_TAB_RANK}
                  </Tab>
                </TabList>
                <Text fontSize='xs' color={mutedColor} pt={2} flexShrink={0}>
                  {tabIndex === 0 ? progressSummary : 'Drag to reorder — #1 is highest'}
                  {tabIndex === 0 && listValid ? ' · Tap Rank teams when ready' : null}
                </Text>
                <TabPanels flex={1} minH={0} overflow='hidden' display='flex' flexDirection='column'>
                  <TabPanel
                    p={0}
                    pt={3}
                    flex={1}
                    minH={0}
                    display='flex'
                    flexDirection='column'
                    overflow='hidden'
                  >
                    <Box
                      flex={1}
                      minH={0}
                      overflowY='auto'
                      overflowX='hidden'
                      pb={2}
                      sx={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      <EditCountryListView
                        countryList={countryList}
                        setCountryList={setCountryList}
                        mobileProps={{ panel: 'selection' }}
                      />
                    </Box>
                  </TabPanel>
                  <TabPanel
                    p={0}
                    pt={3}
                    flex={1}
                    minH={0}
                    display='flex'
                    flexDirection='column'
                    overflow='hidden'
                  >
                    <Box
                      flex={1}
                      minH={0}
                      overflowY='auto'
                      overflowX='hidden'
                      pb={2}
                      sx={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      <EditCountryListView
                        countryList={countryList}
                        setCountryList={setCountryList}
                        mobileProps={{ panel: 'ranking' }}
                      />
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
            {props.prompt ? (
              <Show above='lg'>
                <Text mt={3} fontSize='sm' color={mutedColor}>
                  You can do this later if you&apos;d prefer to.
                </Text>
              </Show>
            ) : null}
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            flexDirection='column'
            gap={2}
            px={3}
            py={3}
            pb={MODAL_FOOTER_PB}
          >
            <Show below='lg'>
              {!listValid ? (
                <Text fontSize='sm' textAlign='center' color={mutedColor}>
                  You must select 10 teams (max 4 seeds, min 2 outsiders).
                </Text>
              ) : null}
            </Show>
            <HStack width='full' align='center' flexWrap='wrap' gap={3}>
              <IconButton
                aria-label='How to play'
                icon={<QuestionIcon fontSize={22} />}
                variant='outline'
                onClick={rulesDisclosure.onOpen}
              />
              <Spacer />
              {props.prompt ? (
                <Button onClick={props.onClose} variant='ghost'>
                  Skip
                </Button>
              ) : null}
              <Show above='lg'>
                <Button
                  isLoading={isBusy}
                  isDisabled={!inputValid}
                  colorScheme='green'
                  onClick={async () => {
                    await handleSubmit();
                  }}
                >
                  Submit
                </Button>
              </Show>
              <Show below='lg'>
                <Button
                  isLoading={isBusy}
                  isDisabled={isFinalTab() ? !inputValid : !listValid}
                  colorScheme='green'
                  onClick={async () => {
                    if (isFinalTab()) {
                      await handleSubmit();
                    } else {
                      setTabIndex(tabIndex + 1);
                    }
                  }}
                >
                  {isFinalTab() ? 'Submit' : 'Next'}
                </Button>
              </Show>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <RulesModal isOpen={rulesDisclosure.isOpen} onClose={rulesDisclosure.onClose} />
      <ConfirmModal
        isOpen={confirmEditDisclosure.isOpen}
        title={'Reset your 10 Teams?'}
        message={
          'WARNING: This is a full RESET.\n\n' +
          '- Your score will be set back to 0.\n' +
          '• Every point you have earned so far will be permanently forfeited.\n' +
          '• You will only earn points from the next Matchday onwards with your new list.\n' +
          '• You will be labelled as a "Latecomer" in all your leagues.\n' +
          '• This cannot be undone.\n\n' +
          'Are you absolutely sure you want to reset?'
        }
        onClose={confirmEditDisclosure.onClose}
        isDestructive
        onConfirm={async () => {
          await submitTeam();
        }}
      />
    </>
  );
}
